import React, { useEffect, useRef, useState } from 'react';
import { format, isToday, isYesterday, isThisMonth, isThisYear, parseISO } from 'date-fns';
import { ChatLine } from './ChatLine';
import { Spinner } from '../common/Spinner';

interface Chat {
  id: string;
  title: string;
  created_at: string;
}

interface ChatListProps {
  chats?: Chat[];
  onChatClick?: (chatId: string) => void;
  onShareChat?: (chatId: string) => void;
  onDeleteChat?: (chatId: string) => void;
  activeChatId?: string;
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
}

type GroupedChats = {
  [key: string]: Chat[];
};

export const ChatList: React.FC<ChatListProps> = ({
  chats = [],
  onChatClick,
  onShareChat,
  onDeleteChat,
  activeChatId,
  hasMore,
  isLoading,
  onLoadMore
}) => {
  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [hasMore, isLoading, onLoadMore]);

  const groupChatsByDate = (chats: Chat[]): GroupedChats => {
    return chats.reduce((groups: GroupedChats, chat) => {
      try {
        const date = parseISO(chat.created_at);
        let groupKey: string;

        if (isToday(date)) {
          groupKey = 'Today';
        } else if (isYesterday(date)) {
          groupKey = 'Yesterday';
        } else if (isThisMonth(date)) {
          groupKey = 'This Month';
        } else if (isThisYear(date)) {
          groupKey = format(date, 'MMMM');
        } else {
          groupKey = format(date, 'MMMM yyyy');
        }

        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(chat);
      } catch (error) {
        console.error('Error processing chat date:', chat, error);
      }
      return groups;
    }, {});
  };

  const groupedChats = groupChatsByDate(chats);
  const sortedGroups = Object.keys(groupedChats).sort((a, b) => {
    const order = ['Today', 'Yesterday', 'This Month'];
    const aIndex = order.indexOf(a);
    const bIndex = order.indexOf(b);
    
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    
    return b.localeCompare(a);
  });

  if (chats.length === 0) {
    return (
      <div className="text-center text-gray-500 text-sm">
        No chats yet
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {sortedGroups.map((group) => (
        <div key={group}>
          <div className="mb-2">
            <h3 className="text-xs font-medium text-[#361E06] px-3">{group}</h3>
          </div>
          <div className="space-y-1">
            {groupedChats[group].map((chat) => (
              <ChatLine
                key={chat.id}
                chat={chat}
                isActive={chat.id === activeChatId}
                onClick={() => onChatClick?.(chat.id)}
                onShare={() => onShareChat?.(chat.id)}
                onDelete={() => onDeleteChat?.(chat.id)}
              />
            ))}
          </div>
        </div>
      ))}
      
      {/* Intersection Observer Target */}
      <div ref={observerTarget} className="h-4 w-full" />
      
      {/* Loading Indicator */}
      {isLoading && (
        <div className="flex justify-center py-4">
          <Spinner className="w-5 h-5 text-gray-400" />
        </div>
      )}
    </div>
  );
};
